import axios, { AxiosError } from "axios";
import type { ActiveCredential } from "./types";
import type { IntegrationInfo, ProviderInfo } from "@/generated";
import type { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function buildActiveCredentials(
  integrations: IntegrationInfo[]
): ActiveCredential[] {
  const rows: ActiveCredential[] = [];
  for (const integration of integrations) {
    for (const provider of integration.providers) {
      for (const cred of provider.credentials ?? []) {
        rows.push({
          key: `${integration.type}::${provider.key}::${cred.id}`,
          credentialId: cred.id,
          scope: cred.scope ?? "workspace",
          integration,
          provider,
          displayName: cred.display_name,
          status: cred.status,
          meta: cred.meta,
          connectedBy: cred.connected_by ?? undefined,
          connectedAt: cred.connected_at ?? undefined,
          disabledNodes: cred.disabled_nodes,
          disabledTriggers: cred.disabled_triggers,
        });
      }
    }
  }
  return rows;
}

// ---------------------------------------------------------------------------
// OAuth connect helper
// ---------------------------------------------------------------------------

export async function startOAuthFlow(
  groupId: string,
  integration: IntegrationInfo,
  provider: ProviderInfo,
  returnTo: string,
  credentialId?: string,
  connectionConfig?: Record<string, unknown>
): Promise<string> {
  const resp = await axios.post(
    `/api/backend/groups/${groupId}/integrations/oauth/start`,
    {
      integration_type: integration.type,
      provider_key: provider.key,
      redirect_uri: returnTo,
      ...(credentialId ? { credential_id: credentialId } : {}),
      ...(connectionConfig ? { connection_config: connectionConfig } : {}),
    }
  );
  return resp.data.authorize_url as string;
}

/** OAuth providers whose OAuth app (client ID, secret, instance) is entered on
 *  the connection form before signing in, instead of by an admin. */
export function hasOAuthAppForm(provider: ProviderInfo): boolean {
  return provider.auth_type === "oauth2" && !!provider.config_url;
}

/** Whether every required OAuth app field has a value. On reconnect a blank
 *  secret is allowed: the backend keeps the stored one. */
export function isOAuthAppComplete(
  config: Record<string, NoxusFormEntryConfig>,
  values: Record<string, unknown>,
  keepsSecret = false
): boolean {
  return Object.entries(config).every(([key, entry]) => {
    if (entry.optional) return true;
    if (keepsSecret && entry.display.type === "password") return true;
    const value = values[key];
    return typeof value === "string" ? value.trim() !== "" : value != null;
  });
}

export function oauthStartErrorMessage(error: unknown): string {
  const message =
    error instanceof AxiosError
      ? (error.response?.data as { message?: string } | undefined)?.message
      : undefined;
  return message || "Failed to start OAuth connection. Please try again.";
}

// ---------------------------------------------------------------------------
// Typed OAuth-start error detection
// ---------------------------------------------------------------------------

/** Info surfaced when ``/oauth/start`` refuses because no admin-registered
 *  OAuth client exists for the provider. Backend raises
 *  ``AdminOAuthSetupRequiredError`` with ``identifier`` =
 *  ``admin_oauth_setup_required`` and ``params`` carrying the provider key
 *  and human-readable integration name. */
export type AdminOAuthSetupRequiredDetails = {
  providerKey: string;
  integrationName: string;
  message: string;
};

export function getAdminOAuthSetupRequiredDetails(
  error: unknown
): AdminOAuthSetupRequiredDetails | null {
  if (!(error instanceof AxiosError) || error.response?.status !== 400) {
    return null;
  }
  const data = error.response.data as
    | {
        identifier?: string;
        message?: string;
        params?: { provider_key?: string; integration_name?: string };
      }
    | undefined;
  if (!data || data.identifier !== "admin_oauth_setup_required") {
    return null;
  }
  const providerKey = data.params?.provider_key ?? "";
  const integrationName = data.params?.integration_name ?? "";
  if (!providerKey || !integrationName) return null;
  return {
    providerKey,
    integrationName,
    message: data.message ?? "",
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const MOCK_DESCRIPTIONS: Record<string, string> = {
  netsuite: "Sync contacts and COA; read/write invoices & POs",
  xero: "Create and reconcile invoices, contacts, and payments",
  sage: "Read/write invoices, contacts, and payments",
  "microsoft-dynamics": "Sync customers, items, orders, and invoices",
};

export const POPULAR_TYPES = new Set([
  "gdrive",
  "gmail",
  "gcalendar",
  "gspreadsheets",
  "outlook",
  "outlook_shared",
  "teams",
  "onedrive",
  "sharepoint",
  "slack",
  "notion",
  "jira",
  "hubspot",
  "salesforce",
]);
